// What a stream actually SHOWS, as opposed to what it holds. Pure, DOM-free.
//
// AgentStreamState appends a `turn_boundary` entry on every `turn_completed`
// (state.svelte.ts), and the renderer draws it as a hairline rule with no content —
// the activity curator already calls it "not a user-visible activity". So
// `entries.length` is NOT an answer to "is there any work to look at here", and every
// place that asked it that question got a wrong answer.
//
// That gap IS the provider-subagent drill-down bug, measured live on a session that
// fanned out into 8 subagents: a finished provider subagent whose transcript never
// reached this client still gets ONE synthesized turn_completed
// (ProviderSubagentStore's terminalEventFor fires on the running -> completed edge, so
// an open view settles instead of hanging on "running"), which appends exactly one
// turn_boundary and nothing else. `entries.length` therefore read 1, which
//   1. made the fan-out row look openable (isSubagentRowOpenable), and
//   2. skipped AgentStreamView's `entries.length === 0` empty state,
// so clicking the row opened a panel that drew a hairline and nothing else: blank,
// with not even a line of text saying why. Both sites now count VISIBLE entries
// through this ONE function, so the gate that decides a row is openable and the view
// that decides it has something to render can never disagree again.

// The structural slice this needs. Loose on purpose (same idiom as
// session-subagents.ts's TimelineToolRow): callers pass real StreamEntry values, and
// this module stays free of the store's types.
export interface StreamEntryKind {
  kind: string;
  content?: string;
}

// Entries that carry no content of their own — pure turn structure, rendered as a
// separator. Everything else puts something on screen a person can read.
const STRUCTURAL_ENTRY_KINDS: ReadonlySet<string> = new Set(["turn_boundary"]);

// How many of these entries actually render something. Zero means the surface has
// nothing to show and must say so, however many entries it is technically holding.
export function countVisibleStreamEntries(entries: readonly StreamEntryKind[]): number {
  let count = 0;
  for (const entry of entries) {
    if (!STRUCTURAL_ENTRY_KINDS.has(entry.kind)) count += 1;
  }
  return count;
}

export interface ReplyStreamEntry extends StreamEntryKind {
  id: string;
  messageId?: string;
  aliasIds?: readonly string[];
  promptAuthor?: { kind: "agent" | "human"; email?: string };
}

export interface ReplyViewer {
  viewerEmail?: string | null;
  soleHumanEmail?: string | null;
}

function ownPrompt(entry: ReplyStreamEntry, viewer: ReplyViewer): boolean {
  if (entry.promptAuthor?.kind === "agent") return false;
  const author = entry.promptAuthor?.email ?? viewer.soleHumanEmail;
  return (
    !!author && !!viewer.viewerEmail && author.toLowerCase() === viewer.viewerEmail.toLowerCase()
  );
}

// A provider can stamp messageId after first paint (state.svelte.ts), so a renderer id
// already known from before the stamp still identifies the entry.
function entryIdentities(entry: ReplyStreamEntry, known: ReadonlySet<string>): string[] {
  const durable = [entry.messageId, ...(entry.aliasIds ?? [])].filter((id): id is string => !!id);
  if (!durable.length) return [entry.id];
  return known.has(entry.id) ? [...durable, entry.id] : durable;
}

function replyIdentities(
  entries: readonly ReplyStreamEntry[],
  viewer: ReplyViewer,
  known: ReadonlySet<string>,
): string[][] {
  const replies: string[][] = [];
  let text: string[] = [];
  for (const entry of entries) {
    if (entry.kind === "text" && (entry.content === undefined || entry.content.trim()))
      text.push(...entryIdentities(entry, known));
    else if (entry.kind === "user_message") {
      text = [];
      if (!ownPrompt(entry, viewer))
        replies.push(
          entry.messageId ? entryIdentities(entry, known) : [entry.id, ...(entry.aliasIds ?? [])],
        );
    } else if (entry.kind === "turn_boundary") {
      if (text.length) replies.push(text);
      text = [];
    }
  }
  return replies;
}

// Seed historical identities so recovered windows cannot create arrivals.
export class StreamReplyArrivals {
  private conversation = "";
  // ponytail: grows for a conversation's whole life (cleared only on a switch); cap it to
  // the mounted window's ids if a very long-lived conversation shows memory pressure.
  private known = new Set<string>();
  private initialized = false;
  private hadContent = false;

  observe(
    conversation: string,
    entries: readonly ReplyStreamEntry[],
    viewer: ReplyViewer = {},
  ): number {
    if (this.conversation !== conversation) {
      this.conversation = conversation;
      this.known.clear();
      this.initialized = false;
      this.hadContent = false;
    }
    const replies = replyIdentities(entries, viewer, this.known);
    const baseline =
      !this.initialized ||
      !this.hadContent ||
      this.known.size === 0 ||
      !entries.some((entry) => entryIdentities(entry, this.known).some((id) => this.known.has(id)));
    // A replacement without overlap is a baseline, not proof of live arrivals.
    let anchor = -1;
    for (let i = 0; i < replies.length; i += 1)
      if (replies[i].some((id) => this.known.has(id))) anchor = i;
    let added = 0;
    if (this.initialized && this.hadContent) {
      if (anchor >= 0) added = replies.length - anchor - 1;
    }
    for (const ids of replies) for (const id of ids) this.known.add(id);
    if (baseline) {
      for (const entry of entries) {
        if (entry.kind === "text")
          for (const id of entryIdentities(entry, this.known)) this.known.add(id);
      }
    }
    this.initialized = true;
    this.hadContent ||= entries.some((entry) => entry.kind !== "turn_boundary");
    return added;
  }
}

export interface SendStreamEntry {
  id: string;
  kind: string;
  clientMessageId?: string;
  status?: string;
  rehydrated?: true;
}

// The agent pane's twin of messageArrival's localSend: a prompt this tab just drew for its own
// send (the outbox's optimistic row). A restored, failed or unconfirmed redraw is not a send.
function isLocalSend(entry: SendStreamEntry): boolean {
  return (
    entry.kind === "user_message" &&
    !!entry.clientMessageId &&
    !entry.rehydrated &&
    (entry.status === undefined || entry.status === "pending")
  );
}

// True once per row that FIRST appears as a local send (a later status change never counts).
export class StreamLocalSends {
  private conversation: string | undefined;
  private seen = new Set<string>();

  observe(conversation: string, entries: readonly SendStreamEntry[]): boolean {
    const first = this.conversation !== conversation;
    if (first) {
      this.conversation = conversation;
      this.seen.clear();
    }
    let sent = false;
    for (const entry of entries) {
      if (entry.kind !== "user_message" || !entry.clientMessageId || this.seen.has(entry.id))
        continue;
      this.seen.add(entry.id);
      if (!first && isLocalSend(entry)) sent = true;
    }
    return sent;
  }
}
